import styled from 'styled-components'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'

const Hero = styled.section`
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: calc(var(--header-height) + 48px) 24px 96px;
  position: relative;
  overflow: hidden;
`

const HeroBg = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 30% 20%, var(--color-accent-glow) 0%, transparent 60%),
              radial-gradient(ellipse at 70% 80%, rgba(0, 210, 160, 0.08) 0%, transparent 50%);
  pointer-events: none;
`

const HeroContent = styled.div`
  max-width: 800px;
  text-align: center;
  position: relative;
  z-index: 1;
`

const Eyebrow = styled(motion.p)`
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-accent);
  margin-bottom: 20px;
`

const Title = styled(motion.h1)`
  font-size: clamp(2.2rem, 5vw, 3.5rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.03em;
  margin-bottom: 24px;

  span {
    background: linear-gradient(135deg, var(--color-gradient-start), var(--color-gradient-end));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: var(--color-text-secondary);
  line-height: 1.7;
  max-width: 640px;
  margin: 0 auto 40px;
`

const ButtonRow = styled(motion.div)`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`

const PrimaryButton = styled(Link)`
  padding: 14px 32px;
  background: linear-gradient(135deg, var(--color-gradient-start), var(--color-gradient-end));
  color: white;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
    color: white;
  }
`

const SecondaryButton = styled(Link)`
  padding: 14px 32px;
  background: transparent;
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 500;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: var(--color-bg-card);
    border-color: var(--color-text-muted);
  }
`

export default function HeroSection() {
  return (
    <Hero>
      <HeroBg />
      <HeroContent>
        <Eyebrow
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          AI Agents for Service Businesses
        </Eyebrow>
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Your business deserves
          <br />
          <span>an AI team</span>
        </Title>
        <Subtitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Coaching agents, customer support agents, lead qualification, real estate inquiry;
          all configured for your business and embedded on your website. See the difference 
          in seconds, not months.
        </Subtitle>
        <ButtonRow
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <PrimaryButton to="/platform">See Agent Plans</PrimaryButton>
          <SecondaryButton to="/contact">Book a Discovery Call</SecondaryButton>
        </ButtonRow>
      </HeroContent>
    </Hero>
  )
}
